import { useMemo, useState } from 'react'
import KitchenLoader from './KitchenLoader.jsx'
import { useCateringFollowup } from '../hooks/useCateringFollowup.js'

// CateringFollowupSection — drops into the catering inquiry detail
// surface for status='quoted' inquiries. AI drafts a polite check-in
// message + subject line that the manager copies to their email tool.
//
// CLAUDE.md: never auto-send customer-facing emails. Copy-to-clipboard
// only — no Send button.
//
// State machine (single render path at the bottom, no early returns):
//   idle       — no cache row within 24h; show Generate prompt
//   generating — edge fn in flight; show KitchenLoader
//   cached     — payload from the 24h cache window; show draft
//   ready      — fresh generate() succeeded; show draft
//   empty      — payload._empty (tiny-data nudge OR timeout retryable)
//   error      — calm deterministic fallback per CLAUDE.md
//
// Hook ordering rule (CLAUDE.md error #310): all hooks fire BEFORE any
// conditional return. Render branches are inline-conditional below.

const LOADER_MESSAGES = [
  'Reading the quote…',
  'Drafting a polite nudge…',
  'Choosing a subject line…',
]

function formatCooldown(ms) {
  const s = Math.ceil(ms / 1000)
  return `Wait ${s}s`
}

function timeAgo(iso) {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return ''
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hr ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

export default function CateringFollowupSection({ inquiry }) {
  // HOOKS FIRST (CLAUDE.md error #310). No conditional returns above this block.
  const inquiryId = inquiry?.id || null
  // Scope the ai_briefs cache read to the inquiry's OWN unit (not the
  // operator's first unit) so multi-unit operators hit the same cache row
  // the edge fn writes. See useCateringFollowup.
  const { phase, payload, generatedAt, cooldownRemainingMs, generate, error } =
    useCateringFollowup(inquiryId, inquiry?.restaurant_id || null)

  const [copiedKey, setCopiedKey] = useState(null) // 'subject' | 'message' | null

  // Days since the quote was generated. Drives the <2 days soft-amber
  // hint. quoted_at is stamped by useCateringData.updateInquiry on the
  // status flip to 'quoted'.
  const daysSinceQuote = useMemo(() => {
    if (!inquiry?.quoted_at) return null
    const ms = Date.now() - new Date(inquiry.quoted_at).getTime()
    if (!Number.isFinite(ms) || ms < 0) return 0
    return Math.floor(ms / 86_400_000)
  }, [inquiry?.quoted_at])

  async function handleCopy(key, text) {
    try {
      await navigator.clipboard.writeText(text || '')
      setCopiedKey(key)
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[CateringFollowupSection] clipboard write failed', e)
    }
  }

  const cooldownActive = cooldownRemainingMs > 0
  const generating = phase === 'generating'
  const buttonDisabled = generating || cooldownActive
  const buttonLabel = generating
    ? 'Drafting…'
    : cooldownActive
      ? formatCooldown(cooldownRemainingMs)
      : phase === 'cached' || phase === 'ready' || phase === 'empty'
        ? 'Re-draft'
        : 'Draft follow-up'

  const showIdle    = phase === 'idle'
  const showEmpty   = phase === 'empty'
  const showError   = phase === 'error'
  const showDraft   = (phase === 'ready' || phase === 'cached') &&
                      !!(payload?.message || payload?.suggested_subject_line)
  const showSoftHint = daysSinceQuote !== null && daysSinceQuote < 2

  // SINGLE render path. Branches are inline-conditional, not early returns.
  return (
    <section className="mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="text-[11px] font-medium text-black/50">
            Catering follow-up
          </div>
          <h3 className="mt-0.5 text-sm font-semibold text-black/85">
            Draft follow-up
          </h3>
          <p className="mt-0.5 text-xs text-black/55">
            AI drafts a polite check-in based on your saved quote.
          </p>
        </div>
        <button
          type="button"
          onClick={() => generate(false)}
          disabled={buttonDisabled}
          className={[
            'rounded-md px-2.5 py-1 text-xs font-medium',
            buttonDisabled
              ? 'cursor-not-allowed bg-black/5 text-black/40'
              : 'bg-black text-white hover:bg-black/85',
          ].join(' ')}
        >
          {buttonLabel}
        </button>
      </div>

      {/* SOFT HINT — quoted less than 2 days ago. Tappable still. */}
      {showSoftHint && (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900">
          You quoted this {daysSinceQuote === 0 ? 'today' : `${daysSinceQuote} day${daysSinceQuote === 1 ? '' : 's'} ago`} — give them another day or two before nudging.
        </div>
      )}

      {/* IDLE — prompt the user. */}
      {showIdle && (
        <p className="mt-3 text-sm text-black/65">
          Tap Draft follow-up to generate a warm check-in message and a subject line.
          Copy to your email tool — you stay in control of when it goes out.
        </p>
      )}

      {/* GENERATING — KitchenLoader (never the generic blue spinner). */}
      {generating && (
        <div className="mt-3">
          <KitchenLoader
            messages={LOADER_MESSAGES}
            intervalMs={1800}
            size="md"
            align="col"
          />
        </div>
      )}

      {/* EMPTY — tiny-data nudge OR retryable timeout payload. */}
      {showEmpty && (
        <div className="mt-3 rounded-xl bg-black/5 p-3 text-sm text-black/70">
          {payload?.headline || "We couldn't draft a follow-up right now."}
          {payload?.narrative && (
            <div className="mt-1 text-xs text-black/55">{payload.narrative}</div>
          )}
        </div>
      )}

      {/* ERROR — calm fallback, never raw error text. */}
      {showError && (
        <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50/60 p-3 text-sm text-rose-800">
          {error || "We couldn't draft a follow-up right now."}
          <div className="mt-1 text-xs text-rose-700/80">
            Tap Re-draft in a moment to try again.
          </div>
        </div>
      )}

      {/* READY / CACHED — subject line + message. Copy buttons. */}
      {showDraft && (
        <>
          {payload?.suggested_subject_line && (
            <div className="mt-3 rounded-xl border border-black/10 bg-white p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] font-medium uppercase tracking-wide text-black/50">
                  Subject line
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy('subject', payload.suggested_subject_line)}
                  className="rounded-md bg-black/5 px-2 py-0.5 text-[11px] font-medium text-black/75 hover:bg-black/10"
                >
                  {copiedKey === 'subject' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="mt-1 break-words text-sm text-black/85">
                {payload.suggested_subject_line}
              </div>
            </div>
          )}

          {payload?.message && (
            <div className="mt-2 rounded-xl border border-black/10 bg-white p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] font-medium uppercase tracking-wide text-black/50">
                  Message
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy('message', payload.message)}
                  className="rounded-md bg-black/5 px-2 py-0.5 text-[11px] font-medium text-black/75 hover:bg-black/10"
                >
                  {copiedKey === 'message' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="mt-1 whitespace-pre-wrap break-words text-sm text-black/85">
                {payload.message}
              </div>
            </div>
          )}

          {generatedAt && (
            <div className="mt-2 text-[10px] text-black/40">
              Generated {timeAgo(generatedAt)}
            </div>
          )}
        </>
      )}
    </section>
  )
}
